import type {
  FmAnalysisFact,
  FmAnalysisFormFlowDialogDescriptor,
  FmAnalysisMetric,
  FmAnalysisViewDescriptor,
  FmAnalysisWorkspaceDescriptor,
} from './FmAnalysisWorkspaceRoot.types';
import { assertFmFormFlowPageDescriptor } from './FmFormFlowPage.validation';

const FORBIDDEN = new Set([
  'children',
  'className',
  'style',
  'sections',
  'render',
  'component',
  'fontSize',
  'maxWidth',
]);

function rejectEscapes(value: unknown, path = 'descriptor'): void {
  if (!value || typeof value !== 'object') return;
  if (Array.isArray(value)) {
    value.forEach((item, index) => rejectEscapes(item, `${path}[${index}]`));
    return;
  }
  Object.entries(value as Record<string, unknown>).forEach(([key, child]) => {
    if (path === 'descriptor.map' && key === 'mapData') {
      return;
    }
    if (FORBIDDEN.has(key)) {
      throw new Error(`Invalid analysis workspace: ${path}.${key} is forbidden.`);
    }
    if (typeof child !== 'function') rejectEscapes(child, `${path}.${key}`);
  });
}

function validateMetricView(view: {
  metrics: readonly FmAnalysisMetric[];
  facts: readonly FmAnalysisFact[];
}): void {
  view.metrics.forEach((metric) => {
    if (metric.value !== null && !Number.isFinite(metric.value)) {
      throw new Error(`Invalid analysis workspace: metric ${metric.id} must be finite or null.`);
    }
    if (
      metric.precision !== undefined &&
      (!Number.isInteger(metric.precision) || metric.precision < 0 || metric.precision > 3)
    ) {
      throw new Error(
        `Invalid analysis workspace: metric ${metric.id} precision must be an integer from 0 to 3.`,
      );
    }
  });
  view.facts.forEach((fact) => {
    if (!fact.unavailable && !fact.value.trim()) {
      throw new Error(`Invalid analysis workspace: fact ${fact.id} must be served or unavailable.`);
    }
  });
}

function validateDiameterView(view: Extract<FmAnalysisViewDescriptor, { kind: 'diameter' }>): void {
  if (view.state === 'unavailable') {
    if (view.reference !== null || view.comparison !== null || !view.reason) {
      throw new Error('Invalid analysis workspace: unavailable distribution cannot serve values.');
    }
    return;
  }
  if (!view.reference?.length) {
    throw new Error('Invalid analysis workspace: available distribution requires source values.');
  }
  if (view.state === 'modelled_only' && (view.comparison !== null || !view.reason)) {
    throw new Error(
      'Invalid analysis workspace: modelled-only distribution cannot claim comparison values.',
    );
  }
  [...view.reference, ...(view.comparison ?? [])].forEach((series) => {
    series.bins.forEach((bin) => {
      if (
        !Number.isFinite(bin.diameterCm) ||
        !Number.isFinite(bin.stemsPerHectare) ||
        bin.stemsPerHectare < 0
      ) {
        throw new Error(
          'Invalid analysis workspace: distribution bins must be finite served values.',
        );
      }
    });
  });
}

function validateStructureView(
  view: Extract<FmAnalysisViewDescriptor, { kind: 'structure' }>,
): void {
  if (!view.species.length && !view.unavailableReason) {
    throw new Error('Invalid analysis workspace: missing species structure requires a reason.');
  }
  view.species.forEach((row) => {
    if (!Number.isFinite(row.stemsPerHectare) || row.stemsPerHectare < 0) {
      throw new Error(
        'Invalid analysis workspace: species structure must use served finite values.',
      );
    }
  });
}

function validateRelationshipView(
  view: Extract<FmAnalysisViewDescriptor, { kind: 'relationship' }>,
): void {
  if (!view.groups.length && !view.unavailableReason) {
    throw new Error(
      'Invalid analysis workspace: missing relationship observations require a reason.',
    );
  }
  view.groups.forEach((group) => {
    if (!group.points.length) {
      throw new Error('Invalid analysis workspace: relationship groups cannot be empty.');
    }
    group.points.forEach((point) => {
      if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) {
        throw new Error(
          'Invalid analysis workspace: relationship observations must be served finite values.',
        );
      }
    });
  });
}

function validateSatelliteIndexView(
  view: Extract<FmAnalysisViewDescriptor, { kind: 'satellite-index' }>,
): void {
  validateMetricView(view);
  if (view.coverage !== 'ready' && !view.coverageReason?.trim()) {
    throw new Error(
      'Invalid analysis workspace: satellite index coverage gaps require an owner-facing reason.',
    );
  }
  if (
    view.legend &&
    (!view.legend.index.trim() || !view.legend.lowLabel.trim() || !view.legend.highLabel.trim())
  ) {
    throw new Error(
      'Invalid analysis workspace: satellite index legend requires index, lowLabel, and highLabel.',
    );
  }
}

function validateReferenceDeviationPoint(
  point: Extract<FmAnalysisViewDescriptor, { kind: 'reference-deviation' }>['points'][number],
): void {
  if (point.observed !== null && !Number.isFinite(point.observed)) {
    throw new Error(
      `Invalid analysis workspace: reference-deviation ${point.id} observed must be finite or null.`,
    );
  }
  if (point.reference !== null && !Number.isFinite(point.reference)) {
    throw new Error(
      `Invalid analysis workspace: reference-deviation ${point.id} reference must be finite or null.`,
    );
  }
}

function validateReferenceDeviationView(
  view: Extract<FmAnalysisViewDescriptor, { kind: 'reference-deviation' }>,
): void {
  validateMetricView(view);
  if (view.coverage !== 'ready' && !view.coverageReason?.trim()) {
    throw new Error(
      'Invalid analysis workspace: reference-deviation coverage gaps require an owner-facing reason.',
    );
  }
  view.points.forEach(validateReferenceDeviationPoint);
  if (view.coverage === 'unavailable') {
    return;
  }
  if (!view.points.length) {
    throw new Error(
      'Invalid analysis workspace: served reference-deviation views require the window points, including explicit gaps.',
    );
  }
  if (view.coverage === 'ready' && !view.points.some((point) => point.observed !== null)) {
    throw new Error(
      'Invalid analysis workspace: ready reference-deviation cannot have only gap observations.',
    );
  }
}

const VIEW_KINDS: ReadonlySet<string> = new Set([
  'summary',
  'volume',
  'diameter',
  'structure',
  'relationship',
  'data',
  'satellite-index',
  'reference-deviation',
]);

function validateView(view: FmAnalysisViewDescriptor): void {
  if (!VIEW_KINDS.has(view.kind)) {
    throw new Error(`Invalid analysis workspace: view kind "${view.kind}" is not registered.`);
  }
  if (view.kind === 'summary' || view.kind === 'volume') {
    validateMetricView(view);
    return;
  }
  if (view.kind === 'diameter') {
    validateDiameterView(view);
    return;
  }
  if (view.kind === 'structure') {
    validateStructureView(view);
    return;
  }
  if (view.kind === 'relationship') {
    validateRelationshipView(view);
    return;
  }
  if (view.kind === 'satellite-index') {
    validateSatelliteIndexView(view);
    return;
  }
  if (view.kind === 'reference-deviation') {
    validateReferenceDeviationView(view);
  }
}

function validateActions(descriptor: FmAnalysisWorkspaceDescriptor): void {
  const actionIds = new Set(descriptor.actions.map(({ id }) => id));
  if (actionIds.size !== descriptor.actions.length) {
    throw new Error('Invalid analysis workspace: action ids must be unique.');
  }
  descriptor.actions.forEach((action) => {
    if (!action.id.trim() || !action.label.trim()) {
      throw new Error('Invalid analysis workspace: action id and label are required.');
    }
    if (action.disabledReason !== undefined && !action.disabledReason.trim()) {
      throw new Error(
        `Invalid analysis workspace: action ${action.id} disabled reason must be plain text.`,
      );
    }
    if (action.group !== undefined && action.group !== 'chrome' && action.group !== 'transport') {
      throw new Error(`Invalid analysis workspace: action ${action.id} group is not governed.`);
    }
  });
}

function validateTimeSeries(descriptor: FmAnalysisWorkspaceDescriptor): void {
  const transport = descriptor.map.timeSeries;
  if (!transport) return;
  const frameIds = new Set(transport.frames.map(({ id }) => id));
  if (frameIds.size !== transport.frames.length) {
    throw new Error('Invalid analysis workspace: time-series frame ids must be unique.');
  }
  if (transport.frames.length === 0) {
    throw new Error('Invalid analysis workspace: time-series transport requires served frames.');
  }
  if (!frameIds.has(transport.activeId)) {
    throw new Error('Invalid analysis workspace: time-series active frame must be served.');
  }
  if (!transport.positionLabel.trim() || !transport.currentDateLabel.trim()) {
    throw new Error('Invalid analysis workspace: time-series position and date labels are required.');
  }
  const actionIds = new Set(descriptor.actions.map(({ id }) => id));
  [
    transport.playActionId,
    transport.stopActionId,
    transport.previousActionId,
    transport.nextActionId,
  ].forEach((actionId) => {
    if (!actionIds.has(actionId)) {
      throw new Error(
        `Invalid analysis workspace: time-series action ${actionId} must be served.`,
      );
    }
  });
  // Stop is a live command only while the series plays; an enabled Stop on a
  // paused series reads as a second play button.
  const stop = descriptor.actions.find(({ id }) => id === transport.stopActionId);
  if (stop && Boolean(stop.disabledReason) === transport.playing) {
    throw new Error(
      'Invalid analysis workspace: the time-series stop action is enabled only while playing.',
    );
  }
  if (descriptor.map.fitCoverage && descriptor.map.fitCoverage !== 'cover' && descriptor.map.fitCoverage !== 'contain') {
    throw new Error('Invalid analysis workspace: fit coverage must be cover or contain.');
  }
}

function validateOwnedFormFlow(dialog: FmAnalysisFormFlowDialogDescriptor): void {
  const nestedMap = dialog.flow.blocks.find(({ control }) => control.type === 'map');
  if (nestedMap) {
    throw new Error(
      `Invalid analysis workspace: form-flow dialog "${dialog.id}" cannot mount a second map.`,
    );
  }
}

function validateDialogs(descriptor: FmAnalysisWorkspaceDescriptor): void {
  const dialogs = descriptor.dialogs ?? [];
  const dialogIds = new Set(dialogs.map(({ id }) => id));
  if (dialogIds.size !== dialogs.length) {
    throw new Error('Invalid analysis workspace: dialog ids must be unique.');
  }
  if (dialogs.length > 0 && !descriptor.onEvent) {
    throw new Error('Invalid analysis workspace: served dialogs require onEvent.');
  }
  dialogs.forEach((dialog) => {
    if (!dialog.id.trim() || !dialog.title.trim() || !dialog.closeLabel.trim()) {
      throw new Error('Invalid analysis workspace: dialog id, title and close label are required.');
    }
    const closeConfirmation = dialog.closeConfirmation;
    if (closeConfirmation) {
      (['title', 'description', 'confirmLabel', 'cancelLabel'] as const).forEach((field) => {
        if (!closeConfirmation[field].trim()) {
          throw new Error(
            `Invalid analysis workspace: dialog ${dialog.id} close confirmation ${field} is required.`,
          );
        }
      });
    }
    assertFmFormFlowPageDescriptor(dialog.flow);
    validateOwnedFormFlow(dialog);
  });
}

function validateMapContract(descriptor: FmAnalysisWorkspaceDescriptor): void {
  if (
    descriptor.map.selectedPlotId !== null &&
    !descriptor.map.plotRefs.some(({ id }) => id === descriptor.map.selectedPlotId)
  ) {
    throw new Error('Invalid analysis workspace: selected plot must be served.');
  }
  if (
    descriptor.map.datedRaster?.status === 'unavailable' &&
    !descriptor.map.datedRaster.reason.trim()
  ) {
    throw new Error('Invalid analysis workspace: unavailable dated raster requires a reason.');
  }
  if (descriptor.state === 'ready' && descriptor.map.mapData === null) {
    throw new Error('Invalid analysis workspace: ready state requires map data.');
  }
}

export function validateAnalysisWorkspaceDescriptor(
  descriptor: FmAnalysisWorkspaceDescriptor,
): void {
  rejectEscapes(descriptor);
  if (!descriptor.title.trim() || !descriptor.summary.trim()) {
    throw new Error('Invalid analysis workspace: title and summary are required.');
  }
  if (descriptor.views.length === 0) {
    throw new Error('Invalid analysis workspace: at least one view is required.');
  }
  if (!descriptor.views.some(({ id }) => id === descriptor.activeViewId)) {
    throw new Error('Invalid analysis workspace: active view must be served.');
  }
  if (descriptor.activePane !== 'map' && descriptor.activePane !== 'analysis') {
    throw new Error('Invalid analysis workspace: active pane must be governed.');
  }
  if (
    (descriptor.state === 'partial' || descriptor.state === 'stale') &&
    descriptor.stateReasons.length === 0
  ) {
    throw new Error(
      'Invalid analysis workspace: partial and stale states require owner-facing reasons.',
    );
  }
  const selectorIds = new Set(descriptor.selectors.map(({ id }) => id));
  if (selectorIds.size !== descriptor.selectors.length) {
    throw new Error('Invalid analysis workspace: selector ids must be unique.');
  }
  descriptor.selectors.forEach((selector) => {
    if (!selector.options.some(({ id }) => id === selector.value)) {
      throw new Error(`Invalid analysis workspace: selector ${selector.id} value is not served.`);
    }
    if (
      selector.priority !== undefined &&
      selector.priority !== 'primary' &&
      selector.priority !== 'secondary'
    ) {
      throw new Error(`Invalid analysis workspace: selector ${selector.id} priority is not governed.`);
    }
  });
  if (
    descriptor.selectors.some((selector) => selector.priority === 'secondary') &&
    !descriptor.copy.filters?.trim()
  ) {
    throw new Error('Invalid analysis workspace: secondary selectors require filters copy.');
  }
  validateActions(descriptor);
  validateTimeSeries(descriptor);
  validateDialogs(descriptor);
  descriptor.views.forEach(validateView);
  validateMapContract(descriptor);
}
